import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Edit, Trash2, Plus, Save, X, Loader2, Upload, Images, Crop, Move, ZoomIn, ZoomOut } from "lucide-react";
import CMSLogin from "@/components/CMSLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to normalize image URLs
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  if (url.startsWith('/uploads/')) return url;
  return url;
};

const CMSPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [services, setServices] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [pageImages, setPageImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [uploadTab, setUploadTab] = useState("url"); // url or upload
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingPageImage, setEditingPageImage] = useState(null);
  const [showPageImageDialog, setShowPageImageDialog] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [bulkUploadFiles, setBulkUploadFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  
  // Gallery Albums state
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showAlbumDialog, setShowAlbumDialog] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [albumImages, setAlbumImages] = useState([]);
  const [showAddToAlbumDialog, setShowAddToAlbumDialog] = useState(false);

  useEffect(() => {
    // Check if already authenticated
    const authenticated = localStorage.getItem("cms_authenticated");
    if (authenticated === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [servicesRes, galleryRes, pageImagesRes, albumsRes] = await Promise.all([
        axios.get(`${API}/cms/services`),
        axios.get(`${API}/cms/gallery`),
        axios.get(`${API}/cms/page-images`),
        axios.get(`${API}/cms/albums`),
      ]);
      setServices(servicesRes.data);
      setGalleryImages(galleryRes.data);
      setPageImages(pageImagesRes.data);
      setAlbums(albumsRes.data);
    } catch (error) {
      console.error("Error fetching CMS data:", error);
      toast.error("Failed to load CMS data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAlbumImages = async (albumId) => {
    try {
      const response = await axios.get(`${API}/cms/albums/${albumId}`);
      setAlbumImages(response.data.images || []);
    } catch (error) {
      console.error("Error fetching album images:", error);
      toast.error("Failed to load album images");
    }
  };

  // Service Management
  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const serviceData = {
      category: formData.get('category'),
      name: formData.get('name'),
      price: parseFloat(formData.get('price')),
      description: formData.get('description'),
      includes: formData.get('includes').split('\n').filter(i => i.trim()),
      is_popular: formData.get('is_popular') === 'true',
      display_order: parseInt(formData.get('display_order')),
      active: true
    };

    try {
      if (editingService) {
        await axios.put(`${API}/cms/services/${editingService.id}`, serviceData);
        toast.success("Service updated successfully");
      } else {
        await axios.post(`${API}/cms/services`, serviceData);
        toast.success("Service created successfully");
      }
      setShowServiceDialog(false);
      setEditingService(null);
      fetchData();
    } catch (error) {
      console.error("Error saving service:", error);
      toast.error("Failed to save service");
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    
    try {
      await axios.delete(`${API}/cms/services/${serviceId}`);
      toast.success("Service deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting service:", error);
      toast.error("Failed to delete service");
    }
  };

  // Gallery Management
  const handleFileUpload = async (file) => {
    if (!file) return null;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/cms/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.url;
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleGallerySubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    let imageUrl = formData.get('url');
    
    // If upload tab is selected and file is present, upload it first
    if (uploadTab === 'upload' && uploadedFile) {
      imageUrl = await handleFileUpload(uploadedFile);
      if (!imageUrl) return; // Upload failed
    }
    
    const imageData = {
      url: imageUrl,
      category: formData.get('category'),
      title: formData.get('title'),
      description: formData.get('description'),
      display_order: parseInt(formData.get('display_order'))
    };

    try {
      if (editingImage) {
        await axios.put(`${API}/cms/gallery/${editingImage.id}`, imageData);
        toast.success("Image updated successfully");
      } else {
        await axios.post(`${API}/cms/gallery`, imageData);
        toast.success("Image added successfully");
      }
      setShowGalleryDialog(false);
      setEditingImage(null);
      setUploadedFile(null);
      setUploadPreview(null);
      setUploadTab("url");
      fetchData();
    } catch (error) {
      console.error("Error saving image:", error);
      toast.error("Failed to save image");
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;
    
    try {
      await axios.delete(`${API}/cms/gallery/${imageId}`);
      toast.success("Image deleted successfully");
      fetchData();
      // Refresh album images if viewing an album
      if (selectedAlbum) {
        fetchAlbumImages(selectedAlbum.id);
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  // Bulk Upload Handler
  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      title: file.name.replace(/\.[^/.]+$/, "").replace(/_/g, " "),
      category: "all"
    }));
    setBulkUploadFiles(prev => [...prev, ...newFiles]);
  };

  const removeBulkFile = (index) => {
    setBulkUploadFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const updateBulkFileInfo = (index, field, value) => {
    setBulkUploadFiles(prev => {
      const newFiles = [...prev];
      newFiles[index] = { ...newFiles[index], [field]: value };
      return newFiles;
    });
  };

  const handleBulkUploadToAlbum = async (albumId, albumName) => {
    if (bulkUploadFiles.length === 0) return;
    
    console.log(`Starting bulk upload to album: ${albumName} (${albumId})`);
    setBulkUploading(true);
    let successCount = 0;
    const uploadedImages = [];
    
    for (const fileData of bulkUploadFiles) {
      try {
        // Upload the file first
        const formData = new FormData();
        formData.append('file', fileData.file);
        
        console.log(`Uploading file: ${fileData.file.name}`);
        const uploadResponse = await axios.post(`${API}/cms/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        console.log(`File uploaded, URL: ${uploadResponse.data.url}`);
        uploadedImages.push({
          url: uploadResponse.data.url,
          category: fileData.category || 'all',
          title: fileData.title || fileData.file.name,
          description: fileData.title || fileData.file.name,
          display_order: successCount
        });
        
        successCount++;
      } catch (error) {
        console.error(`Error uploading ${fileData.file.name}:`, error);
      }
    }
    
    // Add all images to the album
    console.log(`Adding ${uploadedImages.length} images to album ${albumId}`);
    console.log('Images data:', JSON.stringify(uploadedImages));
    
    if (uploadedImages.length > 0) {
      try {
        const response = await axios.post(`${API}/cms/albums/${albumId}/images`, uploadedImages);
        console.log('Add to album response:', response.data);
      } catch (error) {
        console.error("Error adding images to album:", error);
        toast.error("Failed to add images to album");
      }
    }
    
    setBulkUploading(false);
    toast.success(`Successfully uploaded ${successCount} images to "${albumName}"`);
    setBulkUploadFiles([]);
    setShowBulkUploadDialog(false);
    
    // Refresh data and album images
    await fetchData();
    if (selectedAlbum) {
      await fetchAlbumImages(selectedAlbum.id);
      // Update selectedAlbum to get new image count
      setSelectedAlbum(prev => prev ? { ...prev, image_count: (prev.image_count || 0) + successCount } : null);
    }
  };

  const handleBulkUpload = async () => {
    if (bulkUploadFiles.length === 0) return;
    
    setBulkUploading(true);
    let successCount = 0;
    
    for (const fileData of bulkUploadFiles) {
      try {
        // Upload the file first
        const formData = new FormData();
        formData.append('file', fileData.file);
        
        const uploadResponse = await axios.post(`${API}/cms/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Create gallery entry
        await axios.post(`${API}/cms/gallery`, {
          url: uploadResponse.data.url,
          category: fileData.category,
          title: fileData.title,
          description: fileData.title,
          display_order: galleryImages.length + successCount
        });
        
        successCount++;
      } catch (error) {
        console.error(`Error uploading ${fileData.file.name}:`, error);
      }
    }
    
    setBulkUploading(false);
    toast.success(`Successfully uploaded ${successCount} of ${bulkUploadFiles.length} images`);
    setBulkUploadFiles([]);
    setShowBulkUploadDialog(false);
    fetchData();
  };

  // Album Management
  const handleAlbumSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const albumData = {
      name: form.name.value,
      description: form.description.value,
      category: form.category.value,
      is_public: form.is_public.checked,
    };

    try {
      if (editingAlbum) {
        await axios.put(`${API}/cms/albums/${editingAlbum.id}`, albumData);
        toast.success("Album updated successfully");
      } else {
        await axios.post(`${API}/cms/albums`, albumData);
        toast.success("Album created successfully");
      }
      setShowAlbumDialog(false);
      setEditingAlbum(null);
      fetchData();
    } catch (error) {
      console.error("Error saving album:", error);
      toast.error("Failed to save album");
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    if (!window.confirm("Are you sure you want to delete this album and all its photos?")) return;
    
    try {
      await axios.delete(`${API}/cms/albums/${albumId}`);
      toast.success("Album deleted successfully");
      setSelectedAlbum(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting album:", error);
      toast.error("Failed to delete album");
    }
  };

  const toggleAlbumVisibility = async (album) => {
    try {
      await axios.put(`${API}/cms/albums/${album.id}`, {
        is_public: !album.is_public
      });
      toast.success(`Album is now ${!album.is_public ? 'public' : 'private'}`);
      fetchData();
    } catch (error) {
      console.error("Error toggling album visibility:", error);
      toast.error("Failed to update album");
    }
  };

  // Simple Image Crop/Position Handler
  const openCropEditor = (image) => {
    setCropImage(image);
    setCropPosition({ x: 0, y: 0 });
    setCropZoom(1);
    setShowCropDialog(true);
  };

  const handleCropSave = async () => {
    // For now, just save the position data (full crop would require canvas processing)
    toast.success("Image position updated");
    setShowCropDialog(false);
    setCropImage(null);
  };

  // Page Images Management
  const handlePageImageSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    let imageUrl = formData.get('url');
    
    // If upload tab is selected and file is present, upload it first
    if (uploadTab === 'upload' && uploadedFile) {
      imageUrl = await handleFileUpload(uploadedFile);
      if (!imageUrl) return;
    }
    
    const imageData = {
      url: imageUrl,
      title: formData.get('title'),
      description: formData.get('description')
    };

    try {
      await axios.put(`${API}/cms/page-images/${editingPageImage.section}`, imageData);
      toast.success("Page image updated successfully");
      setShowPageImageDialog(false);
      setEditingPageImage(null);
      setUploadedFile(null);
      setUploadPreview(null);
      setUploadTab("url");
      fetchData();
    } catch (error) {
      console.error("Error saving page image:", error);
      toast.error("Failed to save page image");
    }
  };

  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.category]) acc[service.category] = [];
    acc[service.category].push(service);
    return acc;
  }, {});

  // If not authenticated, show login
  if (!isAuthenticated) {
    return <CMSLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-[#D4AF37] text-xl">Loading CMS...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="cms-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center mb-12">
          <h1 className="text-5xl font-bold">Content Management System</h1>
        </div>

        <Tabs defaultValue="services" className="space-y-8">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="services">Services & Pricing</TabsTrigger>
            <TabsTrigger value="gallery">Portfolio Gallery</TabsTrigger>
            <TabsTrigger value="page-images">Page Images</TabsTrigger>
            <TabsTrigger value="content">Site Content</TabsTrigger>
          </TabsList>

          {/* Services Tab */}
          <TabsContent value="services" className="space-y-8">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-bold">Manage Services</h2>
              <Button
                onClick={() => {
                  setEditingService(null);
                  setShowServiceDialog(true);
                }}
                className="bg-[#D4AF37] hover:bg-[#b89530]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Service
              </Button>
            </div>

            {Object.entries(groupedServices).map(([category, categoryServices]) => (
              <div key={category} className="space-y-4">
                <h3 className="text-2xl font-bold capitalize">{category} Services</h3>
                <div className="space-y-3">
                  {categoryServices.sort((a, b) => a.display_order - b.display_order).map((service) => (
                    <div
                      key={service.id}
                      className="bg-white p-6 rounded-lg shadow-sm border border-[#F2EBE5]"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-xl font-bold">{service.name}</h4>
                            {service.is_popular && (
                              <span className="text-xs bg-[#D4AF37] text-white px-2 py-1 rounded">⭐ Popular</span>
                            )}
                          </div>
                          <p className="text-2xl font-bold text-[#D4AF37] mb-3">${service.price.toFixed(2)}</p>
                          <p className="text-[#8C8C8C] mb-3">{service.description}</p>
                          {service.includes && service.includes.length > 0 && (
                            <div>
                              <p className="font-semibold mb-2">Includes:</p>
                              <ul className="list-disc list-inside text-sm text-[#8C8C8C] space-y-1">
                                {service.includes.map((item, idx) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingService(service);
                              setShowServiceDialog(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteService(service.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          {/* Gallery Tab - Album Based */}
          <TabsContent value="gallery" className="space-y-8">
            {!selectedAlbum ? (
              // Album List View
              <>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-3xl font-bold">Portfolio Galleries</h2>
                    <p className="text-[#8C8C8C] mt-1">Organize your work into galleries. Click a gallery to add photos.</p>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingAlbum(null);
                      setShowAlbumDialog(true);
                    }}
                    className="bg-[#D4AF37] hover:bg-[#b89530]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Gallery
                  </Button>
                </div>

                {albums.length === 0 ? (
                  <div className="text-center py-16 bg-[#F9F8F6] rounded-lg">
                    <Images className="w-16 h-16 mx-auto text-[#D4AF37] mb-4" />
                    <h3 className="text-xl font-bold mb-2">No Galleries Yet</h3>
                    <p className="text-[#8C8C8C] mb-6">Create your first gallery to start organizing your portfolio</p>
                    <Button
                      onClick={() => {
                        setEditingAlbum(null);
                        setShowAlbumDialog(true);
                      }}
                      className="bg-[#D4AF37] hover:bg-[#b89530]"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Gallery
                    </Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {albums.sort((a, b) => a.display_order - b.display_order).map((album) => (
                      <div 
                        key={album.id} 
                        className={`group relative bg-white rounded-xl shadow-sm border overflow-hidden cursor-pointer transition-all hover:shadow-lg ${
                          !album.is_public ? 'border-dashed border-[#8C8C8C]' : 'border-[#F2EBE5]'
                        }`}
                        onClick={() => {
                          setSelectedAlbum(album);
                          fetchAlbumImages(album.id);
                        }}
                      >
                        {/* Cover Image */}
                        <div className="relative aspect-[4/3] bg-[#F9F8F6]">
                          {album.cover_image ? (
                            <img
                              src={album.cover_image}
                              alt={album.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Images className="w-16 h-16 text-[#D4AF37]/30" />
                            </div>
                          )}
                          
                          {/* Visibility Badge */}
                          <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium ${
                            album.is_public 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {album.is_public ? '🌐 Public' : '🔒 Private'}
                          </div>
                          
                          {/* Photo Count */}
                          <div className="absolute bottom-3 right-3 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                            {album.image_count || 0} photos
                          </div>

                          {/* Hover Actions */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAlbumVisibility(album);
                              }}
                              className="bg-white text-black hover:bg-gray-100"
                              title={album.is_public ? "Make Private" : "Make Public"}
                            >
                              {album.is_public ? '🔒' : '🌐'}
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingAlbum(album);
                                setShowAlbumDialog(true);
                              }}
                              className="bg-white text-black hover:bg-gray-100"
                              title="Edit Gallery"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAlbum(album.id);
                              }}
                              className="bg-red-600 hover:bg-red-700"
                              title="Delete Gallery"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Album Info */}
                        <div className="p-4">
                          <h3 className="font-bold text-lg">{album.name}</h3>
                          <p className="text-sm text-[#8C8C8C] capitalize">{album.category}</p>
                          {album.description && (
                            <p className="text-sm text-[#8C8C8C] mt-1 line-clamp-2">{album.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              // Album Detail View - View/Add Photos
              <>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedAlbum(null);
                        setAlbumImages([]);
                      }}
                    >
                      ← Back to Galleries
                    </Button>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-bold">{selectedAlbum.name}</h2>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          selectedAlbum.is_public 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {selectedAlbum.is_public ? '🌐 Public' : '🔒 Private'}
                        </span>
                      </div>
                      <p className="text-[#8C8C8C] text-sm">{albumImages.length} photos in this gallery</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => toggleAlbumVisibility(selectedAlbum)}
                      className="border-[#D4AF37] text-[#D4AF37]"
                    >
                      {selectedAlbum.is_public ? '🔒 Make Private' : '🌐 Make Public'}
                    </Button>
                    <Button
                      onClick={() => setShowBulkUploadDialog(true)}
                      className="bg-[#D4AF37] hover:bg-[#b89530]"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Add Photos
                    </Button>
                  </div>
                </div>

                {albumImages.length === 0 ? (
                  <div className="text-center py-16 bg-[#F9F8F6] rounded-lg">
                    <Upload className="w-16 h-16 mx-auto text-[#D4AF37] mb-4" />
                    <h3 className="text-xl font-bold mb-2">No Photos Yet</h3>
                    <p className="text-[#8C8C8C] mb-6">Add photos to this gallery to display in your portfolio</p>
                    <Button
                      onClick={() => setShowBulkUploadDialog(true)}
                      className="bg-[#D4AF37] hover:bg-[#b89530]"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Photos
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {albumImages.map((image) => (
                      <div key={image.id} className="group relative">
                        <div className="relative overflow-hidden rounded-lg aspect-square">
                          <img
                            src={image.url}
                            alt={image.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => openCropEditor(image)}
                              className="bg-white text-black hover:bg-gray-100"
                              title="Adjust/Crop"
                            >
                              <Crop className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleDeleteImage(image.id)}
                              className="bg-red-600 hover:bg-red-700"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-[#8C8C8C] mt-1 truncate">{image.title}</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Page Images Tab */}
          <TabsContent value="page-images" className="space-y-8">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-bold">Manage Page Images</h2>
                <p className="text-[#8C8C8C] mt-2">Edit images that appear on your homepage and other pages</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {pageImages.map((image) => (
                <div key={image.id} className="bg-white p-6 rounded-lg shadow-sm border border-[#F2EBE5]">
                  <div className="relative overflow-hidden rounded-lg mb-4 h-64">
                    <img
                      src={image.url}
                      alt={image.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="mb-4">
                    <h4 className="text-xl font-bold mb-1">{image.title}</h4>
                    <p className="text-sm text-[#8C8C8C] mb-2">{image.description}</p>
                    <p className="text-xs text-[#D4AF37] font-medium">Section: {image.section}</p>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingPageImage(image);
                      setShowPageImageDialog(true);
                    }}
                    className="w-full bg-[#D4AF37] hover:bg-[#b89530]"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Change Image
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-8">
            <h2 className="text-3xl font-bold">Manage Site Content</h2>
            <div className="bg-white p-6 rounded-lg">
              <p className="text-[#8C8C8C]">
                Site content editing (homepage text, about sections, etc.) - Coming soon!
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {/* Service Dialog */}
        <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingService ? "Edit Service" : "Add New Service"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleServiceSubmit} className="space-y-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select name="category" defaultValue={editingService?.category || "bridal"}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bridal">Bridal</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                    <SelectItem value="additional">Additional Fees</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="name">Service Name</Label>
                <Input
                  id="name"
                  name="name"
                  defaultValue={editingService?.name}
                  required
                />
              </div>

              <div>
                <Label htmlFor="price">Price ($)</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  defaultValue={editingService?.price}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  rows={3}
                  defaultValue={editingService?.description}
                  required
                />
              </div>

              <div>
                <Label htmlFor="includes">Includes (one per line)</Label>
                <Textarea
                  id="includes"
                  name="includes"
                  rows={5}
                  defaultValue={editingService?.includes?.join('\n')}
                  placeholder="Item 1\nItem 2\nItem 3"
                />
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_popular"
                    name="is_popular"
                    value="true"
                    defaultChecked={editingService?.is_popular}
                  />
                  <Label htmlFor="is_popular" className="cursor-pointer">Mark as Popular</Label>
                </div>

                <div>
                  <Label htmlFor="display_order">Display Order</Label>
                  <Input
                    id="display_order"
                    name="display_order"
                    type="number"
                    defaultValue={editingService?.display_order || 0}
                    className="w-20"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-[#D4AF37] hover:bg-[#b89530] flex-1">
                  <Save className="w-4 h-4 mr-2" />
                  Save Service
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowServiceDialog(false);
                    setEditingService(null);
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Gallery Dialog */}
        <Dialog open={showGalleryDialog} onOpenChange={(open) => {
          setShowGalleryDialog(open);
          if (!open) {
            setUploadedFile(null);
            setUploadPreview(null);
            setUploadTab("url");
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingImage ? "Edit Image" : "Add New Image"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleGallerySubmit} className="space-y-4">
              <div>
                <Label className="mb-2 block">Image Source</Label>
                <div className="flex gap-2 mb-4">
                  <Button
                    type="button"
                    variant={uploadTab === "url" ? "default" : "outline"}
                    onClick={() => setUploadTab("url")}
                    className={uploadTab === "url" ? "bg-[#D4AF37] hover:bg-[#b89530]" : ""}
                  >
                    Image URL
                  </Button>
                  <Button
                    type="button"
                    variant={uploadTab === "upload" ? "default" : "outline"}
                    onClick={() => setUploadTab("upload")}
                    className={uploadTab === "upload" ? "bg-[#D4AF37] hover:bg-[#b89530]" : ""}
                  >
                    Upload from Computer
                  </Button>
                </div>

                {uploadTab === "url" ? (
                  <div>
                    <Label htmlFor="url">Image URL</Label>
                    <Input
                      id="url"
                      name="url"
                      type="url"
                      defaultValue={editingImage?.url}
                      placeholder="https://images.unsplash.com/..."
                      required={uploadTab === "url"}
                    />
                    <p className="text-xs text-[#8C8C8C] mt-1">
                      Enter the full URL of the image
                    </p>
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="file-upload">Upload Image</Label>
                    <div className="mt-2">
                      <input
                        id="file-upload"
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setUploadedFile(file);
                            // Create preview
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setUploadPreview(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="block w-full text-sm text-[#8C8C8C]
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-[#F2EBE5] file:text-[#1A1A1A]
                          hover:file:bg-[#D4AF37] hover:file:text-white
                          cursor-pointer"
                      />
                    </div>
                    {uploadPreview && (
                      <div className="mt-4">
                        <Label className="mb-2 block">Preview:</Label>
                        <img
                          src={uploadPreview}
                          alt="Preview"
                          className="w-full max-h-64 object-cover rounded-lg"
                        />
                      </div>
                    )}
                    <p className="text-xs text-[#8C8C8C] mt-2">
                      Supports: JPG, PNG, WebP, HEIC
                    </p>
                    {/* Hidden input for form submission */}
                    <input type="hidden" name="url" value={uploadPreview || ""} />
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Select name="category" defaultValue={editingImage?.category || "bridal"}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="bridal">Bridal</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  name="title"
                  defaultValue={editingImage?.title}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  defaultValue={editingImage?.description}
                  required
                />
              </div>

              <div>
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  name="display_order"
                  type="number"
                  defaultValue={editingImage?.display_order || 0}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="submit" 
                  className="bg-[#D4AF37] hover:bg-[#b89530] flex-1"
                  disabled={uploading || (uploadTab === "upload" && !uploadedFile)}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Image
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowGalleryDialog(false);
                    setEditingImage(null);
                    setUploadedFile(null);
                    setUploadPreview(null);
                    setUploadTab("url");
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Page Image Dialog */}
        <Dialog open={showPageImageDialog} onOpenChange={(open) => {
          setShowPageImageDialog(open);
          if (!open) {
            setUploadedFile(null);
            setUploadPreview(null);
            setUploadTab("url");
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                Edit Page Image - {editingPageImage?.section}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePageImageSubmit} className="space-y-4">
              <div>
                <Label className="mb-2 block">Image Source</Label>
                <div className="flex gap-2 mb-4">
                  <Button
                    type="button"
                    variant={uploadTab === "url" ? "default" : "outline"}
                    onClick={() => setUploadTab("url")}
                    className={uploadTab === "url" ? "bg-[#D4AF37] hover:bg-[#b89530]" : ""}
                  >
                    Image URL
                  </Button>
                  <Button
                    type="button"
                    variant={uploadTab === "upload" ? "default" : "outline"}
                    onClick={() => setUploadTab("upload")}
                    className={uploadTab === "upload" ? "bg-[#D4AF37] hover:bg-[#b89530]" : ""}
                  >
                    Upload from Computer
                  </Button>
                </div>

                {uploadTab === "url" ? (
                  <div>
                    <Label htmlFor="url">Image URL</Label>
                    <Input
                      id="url"
                      name="url"
                      type="url"
                      defaultValue={editingPageImage?.url}
                      placeholder="https://images.unsplash.com/..."
                      required={uploadTab === "url"}
                    />
                    <p className="text-xs text-[#8C8C8C] mt-1">
                      Enter the full URL of the image
                    </p>
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="file-upload-page">Upload Image</Label>
                    <div className="mt-2">
                      <input
                        id="file-upload-page"
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setUploadedFile(file);
                            // Create preview
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setUploadPreview(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="block w-full text-sm text-[#8C8C8C]
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-[#F2EBE5] file:text-[#1A1A1A]
                          hover:file:bg-[#D4AF37] hover:file:text-white
                          cursor-pointer"
                      />
                    </div>
                    {uploadPreview && (
                      <div className="mt-4">
                        <Label className="mb-2 block">Preview:</Label>
                        <img
                          src={uploadPreview}
                          alt="Preview"
                          className="w-full max-h-64 object-cover rounded-lg"
                        />
                      </div>
                    )}
                    <p className="text-xs text-[#8C8C8C] mt-2">
                      Supports: JPG, PNG, WebP, HEIC
                    </p>
                    {/* Hidden input for form submission */}
                    <input type="hidden" name="url" value={uploadPreview || ""} />
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  name="title"
                  defaultValue={editingPageImage?.title}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  defaultValue={editingPageImage?.description}
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="submit" 
                  className="bg-[#D4AF37] hover:bg-[#b89530] flex-1"
                  disabled={uploading || (uploadTab === "upload" && !uploadedFile)}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Update Image
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPageImageDialog(false);
                    setEditingPageImage(null);
                    setUploadedFile(null);
                    setUploadPreview(null);
                    setUploadTab("url");
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={showBulkUploadDialog} onOpenChange={(open) => {
          setShowBulkUploadDialog(open);
          if (!open) {
            bulkUploadFiles.forEach(f => URL.revokeObjectURL(f.preview));
            setBulkUploadFiles([]);
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedAlbum 
                  ? `Add Photos to "${selectedAlbum.name}"` 
                  : "Upload Photos to Gallery"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Drop Zone */}
              <label className="cursor-pointer">
                <div className="border-2 border-dashed border-[#D4AF37]/50 rounded-lg p-8 text-center hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all">
                  <Upload className="w-12 h-12 mx-auto text-[#D4AF37] mb-4" />
                  <p className="text-lg font-medium mb-2">Drop photos here or click to select</p>
                  <p className="text-sm text-[#8C8C8C]">Supports JPG, PNG, WebP • Multiple files allowed</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleBulkFileSelect}
                  className="hidden"
                />
              </label>

              {/* Selected Files Preview */}
              {bulkUploadFiles.length > 0 && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold">{bulkUploadFiles.length} photo(s) selected</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        bulkUploadFiles.forEach(f => URL.revokeObjectURL(f.preview));
                        setBulkUploadFiles([]);
                      }}
                    >
                      Clear All
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {bulkUploadFiles.map((fileData, index) => (
                      <div key={index} className="relative">
                        <img
                          src={fileData.preview}
                          alt={fileData.title}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeBulkFile(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => {
                    if (selectedAlbum) {
                      handleBulkUploadToAlbum(selectedAlbum.id, selectedAlbum.name);
                    } else {
                      handleBulkUpload();
                    }
                  }}
                  disabled={bulkUploadFiles.length === 0 || bulkUploading}
                  className="bg-[#D4AF37] hover:bg-[#b89530] flex-1"
                >
                  {bulkUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload {bulkUploadFiles.length} Photo(s)
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    bulkUploadFiles.forEach(f => URL.revokeObjectURL(f.preview));
                    setBulkUploadFiles([]);
                    setShowBulkUploadDialog(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Album Create/Edit Dialog */}
        <Dialog open={showAlbumDialog} onOpenChange={(open) => {
          setShowAlbumDialog(open);
          if (!open) setEditingAlbum(null);
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingAlbum ? "Edit Gallery" : "Create New Gallery"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAlbumSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Gallery Name *</Label>
                <Input
                  id="name"
                  name="name"
                  defaultValue={editingAlbum?.name}
                  placeholder="e.g., Summer Wedding Collection"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  defaultValue={editingAlbum?.description}
                  placeholder="Brief description of this gallery..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="category">Category</Label>
                <Select name="category" defaultValue={editingAlbum?.category || "all"}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="bridal">Bridal</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-3 p-4 bg-[#F9F8F6] rounded-lg">
                <input
                  type="checkbox"
                  id="is_public"
                  name="is_public"
                  defaultChecked={editingAlbum?.is_public !== false}
                  className="w-5 h-5 rounded border-[#D4AF37] text-[#D4AF37] focus:ring-[#D4AF37]"
                />
                <div>
                  <Label htmlFor="is_public" className="cursor-pointer font-medium">
                    Public Gallery
                  </Label>
                  <p className="text-sm text-[#8C8C8C]">
                    Public galleries are visible on your portfolio page
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-[#D4AF37] hover:bg-[#b89530] flex-1">
                  <Save className="w-4 h-4 mr-2" />
                  {editingAlbum ? "Save Changes" : "Create Gallery"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAlbumDialog(false);
                    setEditingAlbum(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Image Crop/Position Dialog */}
        <Dialog open={showCropDialog} onOpenChange={(open) => {
          setShowCropDialog(open);
          if (!open) setCropImage(null);
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Adjust Image Position</DialogTitle>
            </DialogHeader>
            
            {cropImage && (
              <div className="space-y-6">
                {/* Preview with Position Controls */}
                <div className="relative bg-[#F9F8F6] rounded-lg overflow-hidden" style={{ height: '300px' }}>
                  <div 
                    className="absolute inset-0 flex items-center justify-center overflow-hidden"
                    style={{
                      transform: `scale(${cropZoom}) translate(${cropPosition.x}px, ${cropPosition.y}px)`
                    }}
                  >
                    <img
                      src={cropImage.url}
                      alt={cropImage.title}
                      className="max-w-none"
                      style={{ maxHeight: '400px' }}
                    />
                  </div>
                  <div className="absolute inset-0 border-2 border-dashed border-[#D4AF37]/50 pointer-events-none" />
                </div>

                {/* Zoom Controls */}
                <div className="flex items-center justify-center gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCropZoom(prev => Math.max(0.5, prev - 0.1))}
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-[#8C8C8C] w-20 text-center">{Math.round(cropZoom * 100)}%</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCropZoom(prev => Math.min(2, prev + 0.1))}
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                </div>

                {/* Position Controls */}
                <div className="flex items-center justify-center gap-2">
                  <Move className="w-4 h-4 text-[#8C8C8C]" />
                  <span className="text-sm text-[#8C8C8C]">Use arrow keys or drag to position</span>
                </div>

                <div className="grid grid-cols-3 gap-2 max-w-[150px] mx-auto">
                  <div />
                  <Button variant="outline" size="sm" onClick={() => setCropPosition(p => ({ ...p, y: p.y + 10 }))}>↑</Button>
                  <div />
                  <Button variant="outline" size="sm" onClick={() => setCropPosition(p => ({ ...p, x: p.x + 10 }))}>←</Button>
                  <Button variant="outline" size="sm" onClick={() => setCropPosition({ x: 0, y: 0 })}>⟲</Button>
                  <Button variant="outline" size="sm" onClick={() => setCropPosition(p => ({ ...p, x: p.x - 10 }))}>→</Button>
                  <div />
                  <Button variant="outline" size="sm" onClick={() => setCropPosition(p => ({ ...p, y: p.y - 10 }))}>↓</Button>
                  <div />
                </div>

                <p className="text-xs text-center text-[#8C8C8C]">
                  Tip: This adjusts how the image displays in the portfolio. The dashed border shows the visible area.
                </p>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    onClick={handleCropSave}
                    className="bg-[#D4AF37] hover:bg-[#b89530] flex-1"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Position
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCropDialog(false);
                      setCropImage(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CMSPage;